import type { OutboxState } from '@jq4race/shared';

import type { OfflineDb } from '../infrastructure/dexie/offline-db.js';

/**
 * canCleanupBlobsFor — pure predicate encoding the ONE hard rule for photo
 * blob cleanup (critical requirement, pwa-offline-first Slice 4): NEVER
 * delete a blob whose outbox item is not synced or discarded. Every other
 * state (including "no outbox row at all yet") blocks cleanup.
 */
export function canCleanupBlobsFor(state: OutboxState | undefined): boolean {
  return state === 'synced' || state === 'discarded';
}

/**
 * cleanupSyncedBlobs — sweeps the `blobs` table, deleting every blob whose
 * linked outbox item (`blobs.outboxId` → `outbox.id`) has reached a terminal
 * state per canCleanupBlobsFor. A blob whose outbox row is missing entirely
 * (e.g. capture happened but enqueue — slice 6 — hasn't run yet) is left
 * alone: "no row" is not "synced/discarded", so the predicate blocks it.
 *
 * Nothing calls this yet in Slice 4 (the outbox table is still unwritten
 * until slice 6's enqueue lands — see sign-out-guard.ts's own note on this).
 * It exists now so the cleanup RULE is testable and enforced from day one,
 * ready for the sync engine (slice 7) to invoke after each successful sync.
 */
export async function cleanupSyncedBlobs(db: OfflineDb): Promise<number> {
  const outboxIds = await db.blobs.orderBy('outboxId').uniqueKeys();
  let deleted = 0;

  for (const outboxId of outboxIds) {
    const id = String(outboxId);
    const item = await db.outbox.get(id);
    if (!canCleanupBlobsFor(item?.state)) continue;

    const count = await db.blobs.where('outboxId').equals(id).count();
    await db.blobs.where('outboxId').equals(id).delete();
    deleted += count;
  }

  return deleted;
}
